import { EditOutlined } from '@mui/icons-material';
import { BaseModal, FormActions, FormSectionTitle } from '@practium/ui';
import type { ProductCostsModalMode } from '../../hooks/usePlanningCosts';
import { useProductCostsForm } from '../../hooks/useProductCostsForm';
import {
	isPriceInformed,
	productKey,
	type PackProduct,
	type ProductPriceMap,
} from '../../utils/productCosts';
import { ProductCostsEditList } from './ProductCostsEditList';
import { ProductCostsView } from './ProductCostsView';
import styles from './ProductCostsModal.module.css';

interface ProductCostsModalProps {
	isOpen: boolean;
	mode: ProductCostsModalMode;
	packName: string;
	products: PackProduct[];
	prices: ProductPriceMap;
	updatedAt: string | null;
	canEdit: boolean;
	onClose: () => void;
	onEdit: () => void;
	onSave: (prices: ProductPriceMap) => void;
}

const updatedAtFormatter = new Intl.DateTimeFormat('pt-BR');

const VIEW_MAX_COLUMNS = 4;
const VIEW_COLUMN_WIDTH_REM = 10;
const VIEW_COLUMN_GAP_REM = 1.5;
const VIEW_HORIZONTAL_PADDING_REM = 3.5;
const VIEW_MIN_WIDTH_REM = 36;
const EDIT_WIDTH = '46rem';

const viewModalWidth = (columns: number) =>
	`${Math.max(
		VIEW_MIN_WIDTH_REM,
		columns * VIEW_COLUMN_WIDTH_REM +
			(columns - 1) * VIEW_COLUMN_GAP_REM +
			VIEW_HORIZONTAL_PADDING_REM
	)}rem`;

export const ProductCostsModal = ({
	isOpen,
	mode,
	packName,
	products,
	prices,
	updatedAt,
	canEdit,
	onClose,
	onEdit,
	onSave,
}: ProductCostsModalProps) => {
	const { entries, errors, set, handleSubmit } = useProductCostsForm({
		isOpen,
		products,
		prices,
		onSave,
	});
	const hasPrices = products.some((product) => isPriceInformed(prices[productKey(product.id)]));
	const isView = mode === 'view';
	const viewColumns = Math.max(1, Math.min(products.length, VIEW_MAX_COLUMNS));
	const editTitle = hasPrices ? 'Editar custo' : 'Adicionar custo';
	const title = `${isView ? 'Custo dos produtos' : editTitle} - ${packName}`;
	const footerInfo = [
		updatedAt ? `Atualizado em ${updatedAtFormatter.format(new Date(updatedAt))}` : '',
		`${products.length} fertilizantes`,
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<BaseModal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			maxWidth={isView ? viewModalWidth(viewColumns) : EDIT_WIDTH}
			actions={
				isView ? (
					<>
						<span className={styles.updatedAt}>{footerInfo}</span>
						<FormActions
							mode="view"
							onClose={onClose}
							onEdit={canEdit ? onEdit : undefined}
							editText="Editar custo"
							editIcon={<EditOutlined />}
						/>
					</>
				) : (
					<FormActions onCancel={onClose} onSave={handleSubmit} saveText="Salvar custos" />
				)
			}
		>
			<div className={styles.section}>
				<FormSectionTitle size="small">Custo dos fertilizantes do combo</FormSectionTitle>
				{isView ? (
					<ProductCostsView products={products} prices={prices} columns={viewColumns} />
				) : (
					<ProductCostsEditList
						products={products}
						entries={entries}
						errors={errors}
						onChange={set}
					/>
				)}
			</div>
		</BaseModal>
	);
};
